import {
  Badge,
  Button,
  Dialog,
  EmptyContent,
  Flex,
  Text,
} from '@/components/ui';
import React, { useMemo, useState } from 'react';
import { DataTable } from '@/components/data-table';
import { IconDownload, IconList, IconTrash } from '@tabler/icons-react';
import { ColumnDef } from '@tanstack/react-table';
import { WhitelistFile } from './types';

interface AllWhitelistsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  whitelists: WhitelistFile[];
  onDownload: (file: WhitelistFile) => void;
  onDelete: (fileId: number) => void;
  loading?: boolean;
  isDeleting?: boolean;
}

export const AllWhitelistsModal: React.FC<AllWhitelistsModalProps> = ({
  open,
  onOpenChange,
  whitelists,
  onDownload,
  onDelete,
  loading = false,
  isDeleting = false,
}) => {
  const [selectedForDelete, setSelectedForDelete] =
    useState<WhitelistFile | null>(null);

  const columns: ColumnDef<WhitelistFile>[] = useMemo(
    () => [
      {
        accessorKey: 'filename',
        header: 'File Name',
        meta: {
          cell: {
            variant: 'fileName',
            showIcon: true,
            endSlot: (row: WhitelistFile) =>
              row.isActive ? (
                <Badge color="green" size="small">
                  Active
                </Badge>
              ) : null,
          },
          width: 'auto',
        },
      },
      {
        accessorKey: 'contentType',
        header: 'Type',
        meta: {
          cell: {
            variant: 'mimeType',
          },
          width: 'auto',
        },
      },
      {
        accessorKey: 'size',
        header: 'Size',
        meta: {
          cell: {
            variant: 'fileSize',
          },
          width: '100px',
        },
      },
      {
        accessorKey: 'uploadedBy',
        header: 'Uploaded By',
        meta: {
          cell: {
            variant: 'user',
            showEmail: false,
          },
          width: '150px',
        },
      },
      {
        accessorKey: 'uploadDate',
        header: 'Date',
        meta: {
          cell: {
            variant: 'datetime',
            relative: false,
          },
          width: 'auto',
        },
      },

      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => (
          <Flex align="center" gap="1" justify="end">
            <Button
              isIconOnly
              variant="text"
              size="small"
              onClick={() => onDownload(row.original)}
              startIcon={<IconDownload />}
              title="Download file"
            />
            <Button
              isIconOnly
              variant="text"
              size="small"
              color="error"
              onClick={() => setSelectedForDelete(row.original)}
              startIcon={<IconTrash />}
              title="Delete whitelist"
              disabled={isDeleting}
            />
          </Flex>
        ),
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: false,
        meta: {
          width: 'auto',
          systemColumn: true,
        },
      },
    ],
    [onDownload, isDeleting, setSelectedForDelete]
  );

  const handleConfirmDelete = () => {
    if (selectedForDelete) {
      onDelete(selectedForDelete.id);
      setSelectedForDelete(null);
    }
  };

  return (
    <>
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Content
          size="xlarge"
          style={{ width: '100%', maxWidth: 'min(1660px, 100%)' }}
          className="all-whitelists-modal"
        >
          <Dialog.Header
            startSlot={<IconList />}
            title="All Whitelists"
            subtitle="View and manage all whitelist files in the system. You can download or delete any whitelist."
          />

          <Dialog.Body>
            {whitelists.length > 0 ? (
              <DataTable
                data={whitelists}
                columns={columns}
                enableSorting={true}
                enableFiltering={true}
                enablePagination={false}
                enableColumnVisibility={false}
                enableRowSelection={false}
                loading={loading}
                searchPlaceholder="Search whitelists..."
                emptyMessage="No whitelists found"
                emptyIcon={<IconList />}
                getRowId={(row: WhitelistFile) => row.id.toString()}
              />
            ) : (
              <EmptyContent
                size="small"
                icon={<IconList />}
                title="No whitelists found"
                description="There are no whitelist files in the system"
              />
            )}
          </Dialog.Body>

          <Dialog.Footer>
            <Button variant="outlined" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>

      {/* Delete Confirmation Dialog */}
      <Dialog.Root
        open={!!selectedForDelete}
        onOpenChange={() => setSelectedForDelete(null)}
      >
        <Dialog.Content size="medium">
          <Dialog.Header title="Delete Whitelist" />

          <Dialog.Body>
            <Text size="2xsmall">
              Are you sure you want to delete{' '}
              <strong>"{selectedForDelete?.filename}"</strong>? This will
              permanently delete the whitelist and all its version history. This
              action cannot be undone.
            </Text>
          </Dialog.Body>

          <Dialog.Footer>
            <Button
              variant="outlined"
              onClick={() => setSelectedForDelete(null)}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="classic"
              highContrast
              onClick={handleConfirmDelete}
              loading={isDeleting}
            >
              Delete Whitelist
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
};
